/**
 * GridArtBanner
 *
 * 120px power-system art banner:
 *   - 3 cooling towers (perspective: large/close left → small/far right)
 *   - Tall chimney stack between towers (straight-sided, like the reference image)
 *   - Slim bar chart in the middle
 *   - 3 smooth data curves: wavy (up/down) but overall trending upward
 *   - House with apex on the RIGHT side; solar panel on the long left slope
 *
 * NOT shown on /login, /register, /create-account.
 */
import { useLocation } from 'react-router-dom';
import styles from './GridArtBanner.module.css';

const EXCLUDED_PATHS = ['/login', '/register', '/create-account'];

// Slim bar heights (px) — alternating green / teal
const BARS: [number, boolean][] = [
  [38, false],[55, false],[44, true], [70, false],[50, false],
  [82, false],[46, false],[66, true], [58, false],[90, false],
  [62, false],[78, false],[48, true], [98, false],[68, false],
  [85, true], [54, false],[104,false],[72, false],[88, true],
  [60, false],[108,false],[76, false],[94, true], [64, false],
  [110,false],[80, false],[96, true], [66, false],[112,false],
  [84, false],[100,true], [70, false],[114,false],[78, false],
  [92, true], [62, false],[108,false],[82, false],[96, false],
];

const BAR_X0 = 340;
const BAR_W  = 8;
const BAR_GAP = 10;   // gap between bars (step = 18)

// Green lollipop data  { stem from y=120 up h px, circle at top }
const GL = [
  { x: 356, h: 50 }, { x: 428, h: 68 }, { x: 500, h: 62 },
  { x: 590, h: 86 }, { x: 680, h: 80 }, { x: 770, h: 98 },
  { x: 860, h: 92 }, { x: 950, h: 110 }, { x: 1040, h: 104 },
];

// Orange lollipop data
const OL = [
  { x: 392, h: 42 }, { x: 464, h: 58 }, { x: 554, h: 72 },
  { x: 644, h: 84 }, { x: 734, h: 76 }, { x: 824, h: 94 },
  { x: 914, h: 88 }, { x: 1004, h: 106 },
];

function BannerSVG(): JSX.Element {
  return (
    <svg
      className={styles.bannerSvg}
      viewBox="0 0 1400 120"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        {/* Warm sky */}
        <linearGradient id="gbSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#8dd4e8" />
          <stop offset="52%"  stopColor="#c8a450" />
          <stop offset="100%" stopColor="#9e7428" />
        </linearGradient>

        {/* Tower 1 — warm concrete */}
        <linearGradient id="gbT1" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#7a5c28" />
          <stop offset="28%"  stopColor="#caa850" />
          <stop offset="62%"  stopColor="#b09040" />
          <stop offset="100%" stopColor="#7a5828" />
        </linearGradient>

        {/* Tower 2 — slightly darker */}
        <linearGradient id="gbT2" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#62481e" />
          <stop offset="40%"  stopColor="#a07a38" />
          <stop offset="100%" stopColor="#58401c" />
        </linearGradient>

        {/* Tower 3 — darkest */}
        <linearGradient id="gbT3" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#504018" />
          <stop offset="50%"  stopColor="#846028" />
          <stop offset="100%" stopColor="#443018" />
        </linearGradient>

        {/* Green bars */}
        <linearGradient id="gbBarG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#4ade80" stopOpacity="0.92" />
          <stop offset="55%"  stopColor="#16a34a" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#065f46" stopOpacity="0.78" />
        </linearGradient>

        {/* Teal bars */}
        <linearGradient id="gbBarT" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#2dd4bf" stopOpacity="0.90" />
          <stop offset="55%"  stopColor="#0d9488" stopOpacity="0.83" />
          <stop offset="100%" stopColor="#065454" stopOpacity="0.76" />
        </linearGradient>

        {/* Solar panel — right slope (faces viewer) */}
        <linearGradient id="gbSolar" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#2563eb" />
          <stop offset="100%" stopColor="#1e3a8a" />
        </linearGradient>

        {/* Solar panel — left slope (faces away, darker) */}
        <linearGradient id="gbSolarL" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#1a4a9a" />
          <stop offset="100%" stopColor="#0f2460" />
        </linearGradient>

        {/* Bottom vignette */}
        <linearGradient id="gbFade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="50%"  stopColor="transparent" />
          <stop offset="100%" stopColor="rgba(4,10,22,0.22)" />
        </linearGradient>
      </defs>

      {/* ── Sky ── */}
      <rect width="1400" height="120" fill="url(#gbSky)" />

      {/* ── Faint graph-paper grid ── */}
      <g stroke="rgba(70,120,160,0.11)" strokeWidth="0.6">
        {[30, 60, 90].map(y => <line key={`h${y}`} x1="0" y1={y} x2="1400" y2={y} />)}
        {[250,350,450,550,650,750,850,950,1050,1150,1250,1350].map(x => (
          <line key={`v${x}`} x1={x} y1="0" x2={x} y2="120" />
        ))}
      </g>

      {/* ══════════════════════════════════════
           COOLING TOWER 1 — largest / closest
           Full 120px height, widest base
           ══════════════════════════════════════ */}
      <path
        d="M 20,122 C 30,86 48,22 46,0 L 116,0 C 114,22 130,86 140,122 Z"
        fill="url(#gbT1)"
      />
      {/* Lit highlight strip */}
      <path
        d="M 46,0 C 50,22 54,86 55,122 L 65,122 C 64,86 60,22 56,0 Z"
        fill="rgba(255,248,215,0.20)"
      />
      {/* Shadow rim */}
      <path
        d="M 116,0 C 112,22 128,86 138,122 L 132,122 C 122,86 106,22 110,0 Z"
        fill="rgba(35,20,6,0.24)"
      />

      {/* ══════════════════════════════════════
           COOLING TOWER 2 — medium / mid-distance
           Starts at y=18
           ══════════════════════════════════════ */}
      <path
        d="M 154,122 C 162,94 174,44 177,20 L 229,20 C 232,44 244,94 252,122 Z"
        fill="url(#gbT2)"
      />
      <path
        d="M 177,20 C 180,44 183,94 184,122 L 192,122 C 191,94 188,44 185,20 Z"
        fill="rgba(255,248,215,0.14)"
      />

      {/* ══════════════════════════════════════
           CHIMNEY — tall, straight stack
           Like the reference image: narrow rectangle,
           dark anthracite color, full banner height
           ══════════════════════════════════════ */}
      {/* Body: slight taper — 10px at base, 7px at top */}
      <path
        d="M 256,122 L 258,0 L 265,0 L 267,122 Z"
        fill="rgba(64,52,38,0.90)"
      />
      {/* Lit face */}
      <path
        d="M 258,0 L 259,0 L 260,122 L 258,122 Z"
        fill="rgba(220,190,130,0.22)"
      />
      {/* Cap brick */}
      <rect x="255" y="0" width="12" height="4" rx="1" fill="rgba(100,80,50,0.80)" />

      {/* ══════════════════════════════════════
           COOLING TOWER 3 — smallest / farthest
           Starts at y=50
           ══════════════════════════════════════ */}
      <path
        d="M 272,122 C 276,108 281,76 284,56 L 316,56 C 318,76 323,108 327,122 Z"
        fill="url(#gbT3)"
      />
      <path
        d="M 284,56 C 286,76 287,108 287,122 L 292,122 C 292,108 291,76 290,56 Z"
        fill="rgba(255,248,215,0.09)"
      />

      {/* ══════════════════════════════════════
           STEAM / SMOKE
           ══════════════════════════════════════ */}
      {/* Tower 1 — large billowing plume */}
      <ellipse cx="78"  cy="6"  rx="34" ry="13" fill="rgba(245,242,234,0.52)" />
      <ellipse cx="56"  cy="2"  rx="22" ry="9"  fill="rgba(245,242,234,0.40)" />
      <ellipse cx="102" cy="3"  rx="20" ry="8"  fill="rgba(245,242,234,0.35)" />
      <ellipse cx="78"  cy="0"  rx="17" ry="6"  fill="rgba(245,242,234,0.25)" />
      {/* Tower 2 — medium */}
      <ellipse cx="202" cy="16" rx="22" ry="8"  fill="rgba(240,237,228,0.46)" />
      <ellipse cx="218" cy="11" rx="14" ry="6"  fill="rgba(240,237,228,0.34)" />
      <ellipse cx="188" cy="13" rx="11" ry="5"  fill="rgba(240,237,228,0.28)" />
      {/* Chimney — thin drifting wisp */}
      <ellipse cx="261" cy="5"  rx="8"  ry="5"  fill="rgba(230,226,216,0.44)" />
      <ellipse cx="258" cy="1"  rx="6"  ry="4"  fill="rgba(230,226,216,0.28)" />

      {/* ══════════════════════════════════════
           SLIM BAR CHART  (40 bars, width=8, step=18)
           ══════════════════════════════════════ */}
      {BARS.map(([h, teal], i) => (
        <rect
          key={i}
          x={BAR_X0 + i * (BAR_W + BAR_GAP)}
          y={120 - h}
          width={BAR_W}
          height={h}
          rx="2"
          fill={teal ? 'url(#gbBarT)' : 'url(#gbBarG)'}
          opacity={0.84}
        />
      ))}

      {/* ══════════════════════════════════════
           LOLLIPOP DATA POINTS
           (stems + circles matching bar zone)
           ══════════════════════════════════════ */}
      {GL.map(({ x, h }, i) => (
        <g key={i}>
          <line x1={x} y1="120" x2={x} y2={120 - h}
            stroke="#22c55e" strokeWidth="1.2" strokeOpacity="0.68" />
          <circle cx={x} cy={120 - h} r="5.5"
            fill="#4ade80" stroke="rgba(255,255,255,0.55)" strokeWidth="1.4" opacity="0.90" />
        </g>
      ))}
      {OL.map(({ x, h }, i) => (
        <g key={i}>
          <line x1={x} y1="120" x2={x} y2={120 - h}
            stroke="#f97316" strokeWidth="1.2" strokeOpacity="0.64" />
          <circle cx={x} cy={120 - h} r="5"
            fill="#fb923c" stroke="rgba(255,255,255,0.48)" strokeWidth="1.4" opacity="0.86" />
        </g>
      ))}

      {/* ══════════════════════════════════════
           HOUSE — full solar roof (both slopes)
           Compact walls; entire roof = solar array.
           Apex: (1325, 46)   Eave: y=80
           Left slope:  (1242,80)→(1325,46) — 83×34px, 22°
           Right slope: (1325,46)→(1408,80) — 83×34px, 22°
           Right face vec (CCW): (-3,8)
           Left  face vec (CCW): (+3,8) [toward viewer from left]
           ══════════════════════════════════════ */}

      {/* Walls */}
      <rect x="1250" y="80" width="150" height="38" fill="#2e3d4e" opacity="0.94" />

      {/* Eave line */}
      <line x1="1242" y1="80" x2="1408" y2="80" stroke="#1a2535" strokeWidth="1.4" />

      {/* ── RIGHT slope solar panel (faces viewer — full slope) ──
          TL=apex(1325,46) TR=r-eave(1408,80) BR=(1405,88) BL=(1322,54) */}
      <polygon points="1325,46 1408,80 1405,88 1322,54"
        fill="url(#gbSolar)" opacity="0.94" />
      <polygon points="1325,46 1408,80 1405,88 1322,54"
        fill="none" stroke="rgba(147,197,253,0.75)" strokeWidth="1.4" />

      {/* Right-slope cell dividers — 2 vertical + 1 horizontal
          Panel vec=(83,34); face=(-3,8)
          t=0.33: top(1352,57) bot(1349,65)
          t=0.67: top(1381,69) bot(1378,77)
          mid-row: (1324,50)→(1407,84) */}
      <line x1="1352" y1="57" x2="1349" y2="65"
        stroke="rgba(147,197,253,0.55)" strokeWidth="0.9" />
      <line x1="1381" y1="69" x2="1378" y2="77"
        stroke="rgba(147,197,253,0.55)" strokeWidth="0.9" />
      <line x1="1324" y1="50" x2="1407" y2="84"
        stroke="rgba(147,197,253,0.55)" strokeWidth="0.9" />
      {/* Glints */}
      <line x1="1327" y1="47" x2="1351" y2="57"
        stroke="rgba(210,235,255,0.32)" strokeWidth="0.9" />
      <line x1="1355" y1="58" x2="1380" y2="68"
        stroke="rgba(210,235,255,0.26)" strokeWidth="0.9" />
      <line x1="1383" y1="70" x2="1407" y2="80"
        stroke="rgba(210,235,255,0.20)" strokeWidth="0.9" />

      {/* ── LEFT slope solar panel (faces away — darker tint) ──
          TL=l-eave(1242,80) TR=apex(1325,46) BR=(1328,54) BL=(1245,88) */}
      <polygon points="1242,80 1325,46 1328,54 1245,88"
        fill="url(#gbSolarL)" opacity="0.88" />
      <polygon points="1242,80 1325,46 1328,54 1245,88"
        fill="none" stroke="rgba(110,160,220,0.55)" strokeWidth="1.2" />

      {/* Left-slope cell dividers
          Panel vec=(83,-34); face=(3,8)
          t=0.33: top(1269,69) bot(1272,77)
          t=0.67: top(1298,57) bot(1301,65)
          mid-row: (1244,84)→(1327,50) */}
      <line x1="1269" y1="69" x2="1272" y2="77"
        stroke="rgba(110,160,220,0.42)" strokeWidth="0.9" />
      <line x1="1298" y1="57" x2="1301" y2="65"
        stroke="rgba(110,160,220,0.42)" strokeWidth="0.9" />
      <line x1="1244" y1="84" x2="1327" y2="50"
        stroke="rgba(110,160,220,0.42)" strokeWidth="0.9" />

      {/* Ridge cap */}
      <circle cx="1325" cy="46" r="2.5" fill="#4a6070" opacity="0.90" />

      {/* ══════════════════════════════════════
           SMOOTH DATA CURVES — cubic bezier
           At every crest/trough the tangent is
           horizontal → smooth rounded turns.
           All three lines trend upward overall
           while clearly oscillating up & down.
           ══════════════════════════════════════ */}

      {/* Yellow — steepest overall rise, clear up/down waves
          Crests at x≈150,335,540,755,970,1185
          Each segment: C (trough+1/3dx),trough_y  (crest-1/3dx),crest_y  crest */}
      <path
        d="M 45,110
           C 80,110  115,88   150,88
           C 175,88  200,100  225,100
           C 262,100 298,72   335,72
           C 367,72  398,86   430,86
           C 467,86  503,58   540,58
           C 575,58  610,74   645,74
           C 682,74  718,46   755,46
           C 790,46  825,62   860,62
           C 897,62  933,34   970,34
           C 1005,34 1040,50  1075,50
           C 1112,50 1148,22  1185,22
           C 1220,22 1255,38  1290,38
           C 1327,38 1363,14  1400,14"
        fill="none" stroke="#fbbf24" strokeWidth="2.8"
        strokeLinecap="round" opacity="0.96"
      />

      {/* Teal — moderate rise, phase offset from yellow */}
      <path
        d="M 70,107
           C 105,107 140,92   175,92
           C 207,92  238,105  270,105
           C 307,105 343,78   380,78
           C 413,78  445,93   478,93
           C 515,93  551,68   588,68
           C 621,68  655,83   688,83
           C 725,83  761,57   798,57
           C 831,57  865,72   898,72
           C 935,72  971,48   1008,48
           C 1041,48 1075,62  1108,62
           C 1145,62 1181,38  1218,38
           C 1251,38 1285,52  1318,52
           C 1345,52 1373,36  1400,36"
        fill="none" stroke="#22d3ee" strokeWidth="2.2"
        strokeLinecap="round" opacity="0.90"
      />

      {/* Orange — gentlest rise, widest waves, long period */}
      <path
        d="M 95,112
           C 137,112 178,100  220,100
           C 257,100 293,114  330,114
           C 370,114 410,94   450,94
           C 487,94  523,108  560,108
           C 598,108 637,88   675,88
           C 712,88  748,103  785,103
           C 823,103 862,82   900,82
           C 937,82  973,97   1010,97
           C 1048,97 1087,77  1125,77
           C 1162,77 1198,92  1235,92
           C 1273,92 1312,72  1350,72
           C 1367,72 1383,66  1400,66"
        fill="none" stroke="#fb923c" strokeWidth="2.2"
        strokeLinecap="round" opacity="0.90"
      />

      {/* ── Yellow dots at wave crests ── */}
      {([[150,88],[335,72],[540,58],[755,46],[970,34],[1185,22]] as [number,number][]).map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r="5.2"
          fill="#fbbf24" stroke="rgba(255,252,180,0.72)" strokeWidth="1.8" />
      ))}

      {/* ── Teal dots at wave crests ── */}
      {([[175,92],[380,78],[588,68],[798,57],[1008,48],[1218,38]] as [number,number][]).map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r="4.8"
          fill="#22d3ee" stroke="rgba(190,248,255,0.68)" strokeWidth="1.6" />
      ))}

      {/* ── Orange dots at wave crests ── */}
      {([[220,100],[450,94],[675,88],[900,82],[1125,77],[1350,72]] as [number,number][]).map(([x,y],i) => (
        <circle key={i} cx={x} cy={y} r="4.8"
          fill="#fb923c" stroke="rgba(255,220,180,0.65)" strokeWidth="1.6" />
      ))}

      {/* ── Bottom vignette ── */}
      <rect width="1400" height="120" fill="url(#gbFade)" />
    </svg>
  );
}

export default function GridArtBanner(): JSX.Element | null {
  const location = useLocation();
  if (EXCLUDED_PATHS.includes(location.pathname)) return null;
  return (
    <div className={styles.banner} aria-hidden="true">
      <BannerSVG />
    </div>
  );
}
